import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { request, switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { ErrorState, Loading } from "../States";
import { percent, usd } from "./format";

/** `GET /spend`. Untyped in the contract (the route returns a plain dict), so spelled out here. */
export interface SpendReport {
  paid_pages: number;
  model_calls: number;
  web_queries: number;
  page_fetches: number;
  usd: number;
  events: number;
  ceiling_usd: number;
  remaining_usd: number;
  used_fraction: number | null;
}

/** At or above this share of the ceiling the screen says so. */
export const NEAR_CEILING = 0.8;

export function fetchSpend(): Promise<SpendReport> {
  return request<SpendReport>("/spend");
}

const count = (n: number) => n.toLocaleString("en-US");

export function SpendView({ report }: { report: SpendReport }) {
  const used = report.used_fraction;
  const capped = used === null ? null : Math.min(1, Math.max(0, used));
  const tone = used === null ? "" : used >= 1 ? "fail" : used >= NEAR_CEILING ? "review" : "ok";
  return (
    <div className="stack">
      <div className="card">
        <div className="spend-head">
          <div>
            <div className="spend-big num">{usd(report.usd)}</div>
            <div className="muted">
              {report.ceiling_usd > 0 ? <>spent of the {usd(report.ceiling_usd)} ceiling</> : <>spent so far</>}
            </div>
          </div>
          {report.ceiling_usd > 0 && (
            <div className="spend-left">
              <div className="num">{usd(report.remaining_usd)}</div>
              <div className="muted">left</div>
            </div>
          )}
        </div>
        {capped === null || used === null ? (
          <p className="muted">No spend ceiling is set on this environment, so there is no share to show.</p>
        ) : (
          <>
            <div className={`progress meter ${tone}`} role="meter" aria-label="Share of the spend ceiling used"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={Number((used * 100).toFixed(1))}>
              <i style={{ width: `${capped * 100}%` }} />
            </div>
            <p className="muted"><span className="num">{percent(used)}</span> of the ceiling used</p>
            {used >= NEAR_CEILING && (
              <p className={`spend-warn ${tone}`} role="status">
                <AlertTriangle size={14} aria-hidden="true" />{" "}
                {used >= 1
                  ? "Over the ceiling. New paid work is refused until the ceiling is raised."
                  : "Close to the ceiling. Any job that would carry the total past it is refused before it starts."}
              </p>
            )}
          </>
        )}
      </div>
      <div className="tiles">
        <Tile n={count(report.paid_pages)} l="Paid pages parsed" />
        <Tile n={count(report.model_calls)} l="Model calls" />
        <Tile n={count(report.web_queries)} l="Web searches" />
        <Tile n={count(report.page_fetches)} l="Pages fetched" />
        <Tile n={count(report.events)} l="Spend records" />
      </div>
      <p className="muted">Totals come from the spend log, so they match the audit record and survive a restart.</p>
    </div>
  );
}

function Tile({ n, l }: { n: string; l: string }) {
  return (
    <div className="card tile">
      <div className="l">{l}</div>
      <div className="n num">{n}</div>
    </div>
  );
}

export function SpendTab() {
  const query = useQuery({ queryKey: ["admin", "spend"], queryFn: fetchSpend });
  const off = switchedOff(query.error, [404]);
  if (query.isPending) return <Loading what="Reading the spend log" />;
  if (off !== null) return <FeatureOff feature="Spend reporting" reason={off} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return <SpendView report={query.data} />;
}
